import { useSensors, useSensor, TouchSensor, MouseSensor, KeyboardSensor, DndContext } from '@dnd-kit/core';
import { useRef, useState, useMemo } from 'react';
import { useFixedPM2HeaderWhileRefIsInView } from '@pm2/react-utils';
import { inTextDropzoneInput } from '@utils/component-framework';
import { NoSSR } from '@utils/react';
import { useComponentRootRef } from '../../../../context.js';
import { useUpdateTaskEntryMutation } from '../../../../state/mutations/update-task-entry.js';
import { TaskAutoScroller } from '../../../../utils/dnd-kit.auto-scroll.js';
import { makeRestrictToRef } from '../../../../utils/dnd-kit.restrict-to-container.js';
import { OpgavekompleksetTaskDragOverlay } from '../../../atom.drag-overlay/_drag-overlay.js';
import { PlaceItemsInTextTile } from './atom.tile.js';
import { TileDrawer } from './molecule.drawer/_drawer.js';
import { DropzoneContainer } from './molecule.dropzones.js';
import { wrapper } from './organism.input-container.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';

function InputContainer(props) {
  const ref = useRef(null);
  const componentRef = useComponentRootRef();
  const [draggedTileId, setDraggedTileId] = useState();
  const update = useUpdateTaskEntryMutation();
  const sensors = useSensors(useSensor(TouchSensor, {
    activationConstraint: {
      distance: 6
    }
  }), useSensor(MouseSensor, {
    activationConstraint: {
      distance: 6
    }
  }), useSensor(KeyboardSensor, {
    scrollBehavior: "instant"
  }));
  const handleDragStart = event => {
    setDraggedTileId(event.active.id.toString().replace(/-[^-]*?$/, ""));
  };
  const handleDragEnd = event => {
    setDraggedTileId(undefined);
    if (!event.over?.id) {
      return;
    }
    const tileId = event.active.id.toString().replace(/-[^-]*?$/, "");
    const activeTile = props.task.tiles.find(it => it.id === tileId);
    if (!activeTile) {
      console.warn("Unable to resolve active tile by id", tileId);
      return;
    }
    if (event.over.id === "drawer") {
      update(props.task.id, prev => {
        const next = prev ?? {};
        for (const [nodeId, entry] of Object.entries(next)) {
          const dropzoneEntry = inTextDropzoneInput.entry.asTypeSafe(entry);
          if (dropzoneEntry && dropzoneEntry.tileId === tileId) {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete next[nodeId];
          }
        }
        return next;
      });
    } else {
      const targetDropzone = props.task.content.find(it => it.nodeType === "in-text-dropzone-input" && it.id === event.over?.id);
      if (!targetDropzone) {
        return;
      }
      update(props.task.id, prev => {
        const next = prev ?? {};
        if (inTextDropzoneInput.entry.asType(prev?.[targetDropzone.id])?.tileId === tileId) {
          return next;
        }

        //
        // STEP: Determine previous dropzone for tile if any and remove the
        // tile from there.
        //
        let previousDropzoneIdForTile = undefined;
        for (const [dropzoneId, entry] of Object.entries(next)) {
          const dropzoneEntry = inTextDropzoneInput.entry.asTypeSafe(entry);
          if (dropzoneEntry && dropzoneEntry.tileId === tileId) {
            previousDropzoneIdForTile = dropzoneId;
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete next[dropzoneId];
          }
        }

        //
        // STEP: Move current tile in the dropzone to the dropzone that the new
        // tile was currently in.
        //
        const previousTileInNewDropzone = inTextDropzoneInput.entry.asTypeSafe(next[targetDropzone.id]);
        if (previousDropzoneIdForTile && previousTileInNewDropzone) {
          next[previousDropzoneIdForTile] = previousTileInNewDropzone;
        }

        //
        // STEP: Move tile to target dropzone
        //
        next[targetDropzone.id] = {
          type: "in-text-dropzone",
          tileKey: activeTile.itemKey,
          tileId
        };
        return next;
      });
    }
  };
  const draggedInput = props.task.tiles.find(it => it.id === draggedTileId);
  const restrictToComponent = useMemo(() => makeRestrictToRef(componentRef), [componentRef]);
  useFixedPM2HeaderWhileRefIsInView(ref);
  return /*#__PURE__*/jsx(NoSSR, {
    children: /*#__PURE__*/jsx("div", {
      ref: ref,
      className: wrapper,
      children: /*#__PURE__*/jsxs(DndContext, {
        sensors: sensors,
        onDragStart: handleDragStart,
        onDragEnd: handleDragEnd,
        onDragCancel: handleDragEnd,
        modifiers: [restrictToComponent],
        autoScroll: false,
        children: [/*#__PURE__*/jsx(DropzoneContainer, {
          content: props.task.content,
          draggable: true,
          draggedItemId: draggedTileId,
          facitType: "user-entry",
          taskEntryKey: props.taskEntryKey,
          tiles: props.task.tiles
        }), /*#__PURE__*/jsx(TileDrawer, {
          draggable: true,
          draggedTileId: draggedTileId,
          taskEntryKey: props.taskEntryKey,
          tiles: props.task.tiles,
          type: "user-entry"
        }), /*#__PURE__*/jsx(OpgavekompleksetTaskDragOverlay, {
          dropAnimation: {
            duration: 0
          },
          task: props.task,
          children: draggedInput && /*#__PURE__*/jsx(PlaceItemsInTextTile, {
            context: "overlay",
            state: "input",
            taskEntryKey: props.taskEntryKey,
            isDraggable: true,
            isDragOverlay: true,
            tile: draggedInput
          }, draggedInput.id)
        }), /*#__PURE__*/jsx(TaskAutoScroller, {
          taskRef: ref
        })]
      })
    })
  });
}

export { InputContainer };
//# sourceMappingURL=organism.input-container.js.map
